import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AppShell from "@/components/AppShell";
import OrgScopeEmptyState from "@/components/OrgScopeEmptyState";
import { useAuth } from "@/contexts/AuthContext";
import { useOrgScope } from "@/hooks/useOrgScope";
import api, { formatApiError } from "@/lib/api";
import {
  CheckCircle2,
  Circle,
  Building2,
  Users2,
  Tablet,
  Palette,
  Rocket,
  Database,
  ArrowRight,
  Clock3,
  Sparkles,
  Gauge,
  AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import PreLaunchChecklist from "@/components/PreLaunchChecklist";
import MockedAckBanner from "@/components/MockedAckBanner";

const CHECKLIST_LABELS = {
  welcome: { icon: Sparkles, title: "Welcome tour", desc: "Meet EntryBook and what's inside." },
  first_site: { icon: Building2, title: "Create your first site", desc: "Where will visitors sign in?" },
  first_host_invited: { icon: Users2, title: "Invite your first host", desc: "Hosts receive notifications when guests arrive." },
  kiosk_pairing_viewed: { icon: Tablet, title: "Pair a kiosk", desc: "Generate a 6-digit PIN for your reception iPad." },
  branding: { icon: Palette, title: "Set branding", desc: "Logo, colours, kiosk welcome message." },
  go_live: { icon: Rocket, title: "Mark as go-live", desc: "Flip your site live once everything is set." },
};

function KPI({ label, value, hint, testid }) {
  return (
    <div className="eb-card p-6 flex flex-col gap-2 relative overflow-hidden" data-testid={testid}>
      <div className="text-xs text-navy-500 uppercase tracking-wider">{label}</div>
      <div className="text-3xl font-bold text-navy-900">{value}</div>
      {hint && <div className="text-xs text-navy-500">{hint}</div>}
    </div>
  );
}

export default function Dashboard() {
  const { user, org } = useAuth();
  const { noOrgSuperAdmin, onApiError } = useOrgScope();
  const nav = useNavigate();
  const [checklist, setChecklist] = useState(null);
  const [stats, setStats] = useState({ sites: 0, users: 0, visits7d: 0 });
  const [sub, setSub] = useState(null);
  const [compliance, setCompliance] = useState(null);
  const [loading, setLoading] = useState(true);

  // v1.2 PR-C.2 — when a super_admin lands on /dashboard with no active org
  // context (e.g. they clicked "Exit super admin" from the workspace header,
  // or hit /dashboard directly), render the shared empty-state inline.
  // Previously we hard-redirected to /super-admin, which fought with the
  // new exit affordance — exit → /dashboard → redirect → back to super_admin.

  const load = async () => {
    if (noOrgSuperAdmin) {
      // empty-state renders in place of the dashboard; nothing to fetch.
      setLoading(false);
      return;
    }
    try {
      const [cl, sites, users, subRes, audit, comp] = await Promise.all([
        api.get("/onboarding/checklist"),
        api.get("/sites"),
        api.get("/users"),
        api.get("/subscriptions/me"),
        api.get("/orgs/me/audit", { params: { action: "visit.created", limit: 200 } }).catch(() => ({ data: [] })),
        api.get("/dashboard/compliance-score").catch(() => ({ data: null })),
      ]);
      setChecklist(cl.data.items || {});
      setStats({ sites: sites.data.length, users: users.data.length, visits7d: audit.data.length });
      setSub(subRes.data);
      setCompliance(comp.data);
    } catch (e) {
      onApiError(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noOrgSuperAdmin]);

  const toggleItem = async (key) => {
    try {
      const next = { ...checklist, [key]: !checklist[key] };
      await api.patch("/onboarding/checklist", { items: next });
      setChecklist(next);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const loadDemo = async () => {
    try {
      await api.post("/onboarding/load-sample-data");
      toast.success("Sample data loaded");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const resetDemo = async () => {
    try {
      await api.post("/onboarding/reset-sample-data");
      toast.success("Demo data reset");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (noOrgSuperAdmin) {
    return (
      <AppShell>
        <div className="py-12" data-testid="dashboard-no-org-empty-state">
          <OrgScopeEmptyState
            heading="No organisation context"
            subheading={
              <>
                You&apos;re signed in as a super admin without an active org context.
                Pick an organisation below to view its operational dashboard, or open
                the Super admin workspace for cross-tenant tooling.
              </>
            }
          />
        </div>
      </AppShell>
    );
  }

  if (loading || !checklist) {
    return (
      <AppShell>
        <div className="py-20 text-center text-navy-500" data-testid="dashboard-loading">Loading dashboard…</div>
      </AppShell>
    );
  }

  const completed = Object.values(checklist).filter(Boolean).length;
  const total = Object.keys(CHECKLIST_LABELS).length;

  return (
    <AppShell>
      <div className="animate-fade-in-up">
        <MockedAckBanner />
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="eb-overline mb-2">Welcome back, {user?.name?.split(" ")[0]}</div>
            <h1 className="text-3xl md:text-4xl font-semibold tracking-tight" data-testid="dashboard-heading">
              {org?.name || "Dashboard"}
            </h1>
            <p className="text-sm text-navy-500 mt-1">
              {sub?.status === "trialing" ? (
                <>
                  <span className="inline-flex items-center gap-1 text-orange-600 font-medium">
                    <Clock3 className="w-3.5 h-3.5" /> Free trial
                  </span>{" "}
                  · active plan: <strong>{sub?.plan}</strong>
                </>
              ) : (
                <>Plan: <strong>{sub?.plan}</strong> · status: {sub?.status}</>
              )}
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button className="btn-outline" onClick={loadDemo} data-testid="load-sample-data-btn">
              <Database className="w-4 h-4" /> Load sample data
            </button>
            <button className="btn-ghost" onClick={resetDemo} data-testid="reset-sample-data-btn">
              Reset demo data
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8">
          <KPI label="Sites" value={stats.sites} testid="kpi-sites" />
          <KPI label="Users" value={stats.users} testid="kpi-users" />
          <KPI label="Onboarding" value={`${completed}/${total}`} hint="Checklist progress" testid="kpi-checklist" />
        </div>

        {/* Phase 5 P1 — Compliance score (per site) */}
        {compliance && compliance.length > 0 && (
          <div className="mt-8" data-testid="compliance-score-widget">
            <div className="eb-overline mb-2">Compliance score · last 30 days</div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {compliance.map((c) => {
                const tone = c.score >= 85 ? "success" : c.score >= 60 ? "warning" : "danger";
                const ring = tone === "success" ? "border-success-200 bg-success-50/40"
                            : tone === "warning" ? "border-warning-200 bg-warning-50/40"
                            : "border-danger-200 bg-danger-50/40";
                const dot = tone === "success" ? "text-success-600"
                           : tone === "warning" ? "text-warning-600"
                           : "text-danger-600";
                return (
                  <div key={c.site_id} className={`eb-card p-5 border-2 ${ring}`} data-testid={`compliance-${c.site_id}`}>
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <div className="text-xs text-navy-500 uppercase tracking-wider">{c.site_name}</div>
                        <div className={`text-4xl font-bold ${dot} mt-1`}>{c.score}</div>
                      </div>
                      <Gauge className={`w-10 h-10 ${dot}`} />
                    </div>
                    <ul className="text-xs text-navy-700 space-y-1">
                      <li className="flex items-center justify-between">
                        <span>Override-rate (contractor)</span>
                        <span className="font-mono">{c.breakdown.contractor_override_pct}%</span>
                      </li>
                      <li className="flex items-center justify-between">
                        <span>Watchlist mean-time-to-clear</span>
                        <span className="font-mono">{c.breakdown.watchlist_mean_minutes != null ? `${c.breakdown.watchlist_mean_minutes}m` : "—"}</span>
                      </li>
                      <li className="flex items-center justify-between">
                        <span>Insurance renewed before expiry</span>
                        <span className="font-mono">{c.breakdown.insurance_renewed_pct}%</span>
                      </li>
                    </ul>
                    {tone !== "success" && (
                      <div className={`mt-3 text-xs flex items-center gap-1 font-medium ${tone === "warning" ? "text-warning-700" : "text-danger-700"}`}>
                        <AlertTriangle className="w-3 h-3" />
                        {tone === "warning" ? "Worth a look this week" : "Action required"}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Pre-launch checklist (Module 18.6) */}
        <div className="mt-8">
          <PreLaunchChecklist />
        </div>

        {/* Go-live checklist */}
        <div className="mt-10">
          <div className="flex items-end justify-between mb-4">
            <div>
              <div className="eb-overline">Go-live checklist</div>
              <h2 className="text-xl font-semibold mt-1">Let's get you ready.</h2>
            </div>
            <Link to="/onboarding" className="btn-ghost text-orange-600" data-testid="open-wizard-link">
              Open wizard <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            {Object.entries(CHECKLIST_LABELS).map(([key, meta]) => {
              const done = !!checklist[key];
              const Icon = meta.icon;
              return (
                <button
                  key={key}
                  onClick={() => toggleItem(key)}
                  data-testid={`checklist-item-${key}`}
                  className={`text-left eb-card p-4 flex items-start gap-3 hover:border-orange-500/40 transition-all ${done ? "bg-success/5" : ""}`}
                >
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${done ? "bg-success/15 text-success" : "bg-navy-50 text-navy-600"}`}>
                    {done ? <CheckCircle2 className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className={`font-medium ${done ? "text-navy-500 line-through" : "text-navy-900"}`}>{meta.title}</h3>
                    </div>
                    <p className="text-xs text-navy-500 mt-0.5">{meta.desc}</p>
                  </div>
                  {done ? (
                    <CheckCircle2 className="w-5 h-5 text-success flex-shrink-0" />
                  ) : (
                    <Circle className="w-5 h-5 text-navy-300 flex-shrink-0" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </AppShell>
  );
}
